import React from "react";
import { cn } from "@/lib/utils";

interface StatCardProps {
  /** Leading icon, typically a 12px lucide glyph. */
  icon: React.ReactNode;
  /** Short uppercase label, e.g. "Last activity". */
  label: string;
  /** The headline value. Omit and pass children for a non-text body. */
  value?: string;
  /** Secondary line under the value. */
  sub?: string;
  /** Replaces `value`/`sub` when the box needs richer content. */
  children?: React.ReactNode;
  className?: string;
}

/**
 * One box in the Overview status strip.
 *
 * Presentational only — the caller resolves and formats every value, so this
 * renders whatever it is handed without knowing where it came from.
 */
const StatCard: React.FC<StatCardProps> = ({
  icon,
  label,
  value,
  sub,
  children,
  className,
}) => (
  // `min-w-0` so a long value truncates inside its grid column instead of
  // widening it and breaking the equal thirds.
  <div
    className={cn(
      "border border-border rounded-lg px-3.5 py-2.5 min-w-0",
      className,
    )}
  >
    <div className="flex items-center gap-1.5 mb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
      <span className="flex-none">{icon}</span>
      {label}
    </div>
    {children ?? (
      <>
        {value !== undefined && (
          <div className="text-[15px] font-semibold text-foreground">
            {value}
          </div>
        )}
        {sub && (
          <div className="text-[11px] text-muted-foreground mt-px">{sub}</div>
        )}
      </>
    )}
  </div>
);

export default StatCard;
